// This file updates AllKitchen to include offcanvas filters (cuisines, categories, occasions, etc.)
// and syncs them with the URL using useSearchParams.


import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

const useFetchStrapiData = (collection) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;
  const locale = getLocale();

  useEffect(() => {
    const fetchData = async () => {
      if (!STRAPI_URL || !STRAPI_TOKEN) return;

      const url = new URL(`${STRAPI_URL}/api/recipes`);
      url.searchParams.set("locale", locale);
      url.searchParams.set("filters[recipeStatus][$eq]", "Public");
      url.searchParams.set("sort", "title:asc");
      [
        "ingredients",
        "instructions",
        "nutrition",
        "winePairing",
        "recipe_categories",
        "recipe_cuisines",
        "recipe_occasions",
        "cooking_styles",
        "cooking_techniques",
        "dietary_preferences",
        "recipe_courses",
      ].forEach((field) => {
        url.searchParams.append(`populate[${field}][populate]`, "*");
      });

      try {
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${STRAPI_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const result = await res.json();
        setData(result.data || []);
      } catch (err) {
        console.error("❌ Fetch error:", err);
        setError(err.message);
      }
    };

    fetchData();
  }, [STRAPI_URL, STRAPI_TOKEN, locale]);

  return { data, error };
};

const AllKitchen = () => {
  const { data, error } = useFetchStrapiData("recipes");
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");

  const selectedFilter = (key) => searchParams.getAll(key);

  const updateFilter = (key, value) => {
    const current = new Set(selectedFilter(key));
    if (current.has(value)) current.delete(value);
    else current.add(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    Array.from(current).forEach((v) => newParams.append(key, v));
    setSearchParams(newParams);
  };

  const flattenValues = (obj) => {
    if (Array.isArray(obj)) return obj.map(flattenValues).join(" ");
    if (typeof obj === "object" && obj !== null)
      return Object.values(obj).map(flattenValues).join(" ");
    return obj ?? "";
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchSearch = flattenValues([item.title]).toLowerCase().includes(searchQuery.toLowerCase());

      const hasCategory =
        selectedFilter("category").length === 0 ||
        item.recipe_categories?.some((cat) => selectedFilter("category").includes(cat.id.toString()));

      const hasCuisine =
        selectedFilter("cuisine").length === 0 ||
        item.recipe_cuisines?.some((cui) => selectedFilter("cuisine").includes(cui.id.toString()));

      const hasOccasion =
        selectedFilter("occasion").length === 0 ||
        item.recipe_occasions?.some((occ) => selectedFilter("occasion").includes(occ.id.toString()));

      const hasDiet =
        selectedFilter("diet").length === 0 ||
        item.dietary_preferences?.some((diet) => selectedFilter("diet").includes(diet.id.toString()));

      const hasCourse =
        selectedFilter("course").length === 0 ||
        item.recipe_courses?.some((course) => selectedFilter("course").includes(course.id.toString()));

      return matchSearch && hasCategory && hasCuisine && hasOccasion && hasDiet && hasCourse;
    });
  }, [data, searchParams, searchQuery]);

  const renderFilterGroup = (title, items, paramKey) => {
    const uniqueItems = Array.from(
      new Map(items.map((item) => [item.id, item])).values()
    );

    return (
      <div className="widget mb-4">
        <span className="d-flex eyebrow text-muted mb-2">{title}</span>
        <ul className="list-unstyled">
          {uniqueItems.map((item) => (
            <li key={item.id} className="mt-1">
              <div className="form-check form-check-minimal">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`${paramKey}-${item.id}`}
                  checked={selectedFilter(paramKey).includes(item.id.toString())}
                  onChange={() => updateFilter(paramKey, item.id.toString())}
                />
                <label className="form-check-label" htmlFor={`${paramKey}-${item.id}`}>
                  {item.name || item.title}
                </label>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (error) {
    return <h1>{t("global.error")}: {error}</h1>;
  }

  return (
    <>
      <div className="container mt-5">

		<div className="row g-3 g-md-5 align-items-end mb-5">
          <div className="col-12 text-end">
            <ul className="list-inline">
              <li className="list-inline-item ms-2">
                <a
                  className="underline text-black"
                  data-bs-toggle="offcanvas"
                  href="#offcanvasFilter"
                  role="button"
                  aria-controls="offcanvasFilter"
                >
                  {t("recipes.filters", "Filters")}
                </a>
              </li>
            </ul>
          </div>
        </div>


        <div className="row mb-5 g-3 g-xl-5 align-items-end">
          <div className="col-lg-12">
            <input
              type="text"
              className="form-control mb-3 col-6"
              placeholder={t("recipe.DynamicSearchBar", "Find a recipe")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {filteredData.length === 0 && (
              <ol className="list-group list-group-separated col-12">
                <li className="list-group-item d-flex justify-content-between align-items-start text-black">
                  <span>
                    <h4 style={{ fontWeight: "bold", lineHeight: "90%" }}>
                      {t("search.noResults") || "No results found."}
                    </h4>
                  </span>
                </li>
              </ol>
            )}

            <ol className="list-group list-group-separated col-12">
              {filteredData.map((item) => (
                <a
                  href={`/kitchen/${item.slug}`}
                  className="list-group-item d-flex justify-content-between align-items-start text-black"
                  key={item.id}
                >
                  <span>
                    <h4 style={{ fontWeight: "bold", lineHeight: "90%" }}>{item.title}</h4>
                  </span>
                  <h4 className="mt-0" style={{ fontWeight: "bold", lineHeight: "90%" }}>
                    <i className="bi bi-arrow-right"></i>
                  </h4>
                </a>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Offcanvas Filter */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="offcanvasFilter"
        aria-labelledby="offcanvasFilterLabel"
        aria-hidden="true"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasFilterLabel">
            {t("recipes.filters", "Filters")}
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>
        <div className="offcanvas-body">
			{renderFilterGroup(t("recipes.filters.categories"), [...data.flatMap(d => d.recipe_categories || [])], "category")}
			{renderFilterGroup(t("recipes.filters.cuisines"), [...data.flatMap(d => d.recipe_cuisines || [])], "cuisine")}
			{renderFilterGroup(t("recipes.filters.occasions"), [...data.flatMap(d => d.recipe_occasions || [])], "occasion")}
			{renderFilterGroup(t("recipes.filters.dietaryPreferences"), [...data.flatMap(d => d.dietary_preferences || [])], "diet")}
			{renderFilterGroup(t("recipes.filters.courses"), [...data.flatMap(d => d.recipe_courses || [])], "course")}

        </div>
      </div>
    </>
  );
};

export default AllKitchen;
